import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { classNames } from 'primereact/utils';
import RegionalManagerUserDropdown from './RegionalManagerUserDropdown';
import { useLanguage } from '../../../../../../contexts/LanguageContext';

const TechnicalSupportResponsibleDialog = ({ visible, data, onHide, onSave, responsibilityOrderOptions, nextResponsibilityOrder }) => {
    const { th } = useLanguage();
    const [submitted, setSubmitted] = useState(false);
    const [dateError, setDateError] = useState(false);
    const orderOptions = data ? responsibilityOrderOptions.update : responsibilityOrderOptions.create;

    const [formData, setFormData] = useState({
        id: null,
        siteUseId: 0,
        userId: 0,
        responsibilityOrder: null,
        contractStartDate: null,
        contractEndDate: null,
        description: ''
    });

    useEffect(() => {
        setSubmitted(false);
        setDateError(false);

        if (data) {
            const start = data.contractStartDate ? new Date(data.contractStartDate) : null;
            let end = data.contractEndDate ? new Date(data.contractEndDate) : null;
            if (start && end && end < start) end = start;

            setFormData({
                id: data.id ?? null,
                siteUseId: data.siteUseId || 0,
                userId: data.userId || 0,
                responsibilityOrder: data.responsibilityOrder ?? null,
                contractStartDate: start,
                contractEndDate: end,
                description: data.description || ''
            });
        } else {
            setFormData({
                id: null,
                siteUseId: 0,
                userId: 0,
                responsibilityOrder: nextResponsibilityOrder,
                contractStartDate: null,
                contractEndDate: null,
                description: ''
            });
        }
    }, [data, nextResponsibilityOrder, visible]);

    const handleChange = (field, value) => {
        setFormData((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === 'contractStartDate' && updated.contractEndDate && value && updated.contractEndDate < value) {
                updated.contractEndDate = value;
            }
            if (field === 'contractStartDate' || field === 'contractEndDate') setDateError(false);
            return updated;
        });
    };

    const handleSubmit = () => {
        setSubmitted(true);

        if (!formData.userId || !formData.responsibilityOrder || !formData.contractStartDate) return;

        if (formData.contractEndDate && formData.contractEndDate < formData.contractStartDate) {
            setDateError(true);
            return;
        }

        onSave({ ...formData });
        onHide();
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" outlined onClick={onHide} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSubmit} />
        </div>
    );

    return (
        <Dialog
            header={
                data
                    ? th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.title.update')
                    : th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.title.new')
            }
            visible={visible}
            style={{ width: '45vw' }}
            modal
            onHide={onHide}
            className="p-fluid"
            footer={footer}
        >
            <div className="formgrid grid">
                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.label.user')}</label>
                    <RegionalManagerUserDropdown value={formData.userId} onChange={(id) => handleChange('userId', id)} className={submitted && !formData.userId ? 'p-invalid' : ''} />
                    {submitted && !formData.userId && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.label.responsibilityOrder')}</label>
                    <Dropdown
                        value={formData.responsibilityOrder}
                        options={orderOptions}
                        onChange={(e) => handleChange('responsibilityOrder', e.value)}
                        placeholder={th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.placeholder.responsibilityOrder')}
                        className={classNames({ 'p-invalid': submitted && !formData.responsibilityOrder })}
                        disabled={!data}
                    />
                    {submitted && !formData.responsibilityOrder && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">{th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.label.description')}</label>
                    <InputText
                        value={formData.description}
                        onChange={(e) => handleChange('description', e.target.value)}
                        placeholder={th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.placeholder.description')}
                    />
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.label.contractStartDate')}
                    </label>
                    <Calendar
                        value={formData.contractStartDate}
                        onChange={(e) => handleChange('contractStartDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.placeholder.contractStartDate')}
                        className={classNames({ 'p-invalid': submitted && !formData.contractStartDate })}
                        inputClassName="w-full"
                    />
                    {submitted && !formData.contractStartDate && <small className="p-error">{th('common.required')}</small>}
                </div>

                <div className="field col-12 md:col-6">
                    <label className="font-bold block mb-2">
                        {th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.label.contractEndDate')}
                    </label>
                    <Calendar
                        value={formData.contractEndDate}
                        onChange={(e) => handleChange('contractEndDate', e.value)}
                        dateFormat="dd.mm.yy"
                        showIcon
                        placeholder={th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.placeholder.contractEndDate')}
                        minDate={formData.contractStartDate || null}
                        className={classNames({ 'p-invalid': dateError })}
                        inputClassName="w-full"
                    />
                    {dateError && <small className="p-error">{th('customerAddressBrand.tabs.technicalSupportResponsible.dialog.error.endDateBeforeStartDate')}</small>}
                </div>
            </div>
        </Dialog>
    );
};

export default TechnicalSupportResponsibleDialog;

